import {
  ChangeDetectionStrategy,
  Component,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * Reusable shell for profile tab content. Renders the white rounded card
 * with the project-wide tab header (icon badge + title + optional count
 * pill + optional action slot) and projects the rest of the tab's content
 * below the header.
 *
 * Slots:
 * - default: tab body content.
 * - `[tabAction]`: optional element rendered on the right side of the
 *   header (e.g. a "New X" button or filter switch).
 *
 * Styling notes (kept here so consumers don't re-derive them):
 * - rounded-2xl + a soft layered shadow reads as a modern surface
 *   without the heavy AI-default chrome.
 * - Generous interior padding (p-6 sm:p-8) gives the card air; pair
 *   with `space-y-6` (or wider) on a parent when stacking cards.
 * - The h3 uses Lexend (`font-alternate`) for display polish; body
 *   content inherits the global DM Sans.
 *
 * @see User story #18091 — introduced when promoting the announcements
 * tab and standardising the look of the other student profile tabs.
 */
@Component({
  selector: 'app-profile-tab-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, FontAwesomeModule],
  styles: [
    `
      :host {
        display: block;
      }

      .surface {
        box-shadow:
          0 1px 2px 0 rgba(19, 32, 52, 0.04),
          0 12px 24px -16px rgba(19, 32, 52, 0.08);
      }

      /* Small tinted square behind the section icon. currentColor lets the
         consumer set the hue via a Tailwind text-* class, e.g. text-emerald-600. */
      .icon-badge {
        background: color-mix(in srgb, currentColor 10%, white);
      }
    `,
  ],
  template: `
    <section
      class="surface bg-white rounded-2xl p-6 sm:p-8"
      [attr.data-testid]="testId() ?? null"
    >
      <header class="flex items-center justify-between gap-4 mb-6">
        <div class="flex items-center gap-3 min-w-0">
          @if (icon(); as ic) {
            <span
              [ngClass]="iconColorClass()"
              class="icon-badge inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
              aria-hidden="true"
            >
              <fa-icon [icon]="ic" class="text-sm"></fa-icon>
            </span>
          }
          <h3
            class="m-0 font-alternate text-lg tracking-tight text-mty-body truncate"
          >
            {{ title() | translate }}
          </h3>
          @if ((count() ?? 0) > 0) {
            <span
              data-testid="profile-tab-card-count"
              class="ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-[11px] font-medium leading-none text-mty-accent bg-mty-accent-light/70"
              >{{ count() }}</span
            >
          }
        </div>
        <div class="shrink-0">
          <ng-content select="[tabAction]"></ng-content>
        </div>
      </header>
      <ng-content></ng-content>
    </section>
  `,
})
export class ProfileTabCardComponent {
  title = input.required<string>();
  icon = input<IconDefinition | undefined>(undefined);
  iconColorClass = input<string>('text-mty-accent');
  count = input<number | null>(null);
  /** Optional data-testid attribute on the section root. */
  testId = input<string | null>(null);
}
